import React, { useCallback, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "react-query";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Button,
  TextField,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SendIcon from "@mui/icons-material/Send";
import {
  getPassagensRepasseResumo,
  postPassagensRepasseSolicitacao,
  cancelPassagensRepasseSolicitacao,
} from "../../api/passagensApi";

function fmtMoney(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDateTime(v) {
  if (!v) return "—";
  return new Date(v).toLocaleString("pt-BR");
}

const STATUS_COLOR = {
  pendente: "warning",
  em_processamento: "info",
  pago: "success",
  rejeitado: "error",
  cancelado: "default",
};

export default function PassagensRepassesPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [valor, setValor] = useState("");
  const [observacao, setObservacao] = useState("");
  const [formError, setFormError] = useState("");

  const { data, isLoading, error } = useQuery(["passagens-repasses"], () => getPassagensRepasseResumo());

  const resumo = data?.resumo || {};
  const solicitacoes = data?.solicitacoes || [];
  const lancamentos = data?.lancamentos || [];

  const solicitarMutation = useMutation(
    () => postPassagensRepasseSolicitacao({ valor: Number(valor), observacao }),
    {
      onSuccess: () => {
        setDialogOpen(false);
        setValor("");
        setObservacao("");
        setFormError("");
        queryClient.invalidateQueries(["passagens-repasses"]);
      },
      onError: (err) => {
        setFormError(err?.response?.data?.message || err?.message || "Erro ao solicitar.");
      },
    }
  );

  const cancelMutation = useMutation((id) => cancelPassagensRepasseSolicitacao(id), {
    onSuccess: () => queryClient.invalidateQueries(["passagens-repasses"]),
  });

  const handleSolicitar = useCallback(() => {
    const n = Number(valor);
    if (!Number.isFinite(n) || n <= 0) {
      setFormError(t("passagensRepasses.invalidAmount", { defaultValue: "Informe um valor válido." }));
      return;
    }
    solicitarMutation.mutate();
  }, [valor, solicitarMutation, t]);

  if (isLoading) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: "auto" }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <AccountBalanceWalletIcon color="primary" />
          <Typography variant="h5" fontWeight={700}>
            {t("passagensRepasses.title", { defaultValue: "Repasses XPay" })}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" startIcon={<ListAltIcon />} component={RouterLink} to="/passagens">
            {t("passagensPage.listView", { defaultValue: "Lista de vendas" })}
          </Button>
          <Button size="small" variant="contained" startIcon={<SendIcon />} onClick={() => setDialogOpen(true)}>
            {t("passagensRepasses.request", { defaultValue: "Solicitar repasse" })}
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error" sx={{ mb: 2 }}>{error?.message || "Erro ao carregar."}</Alert> : null}
      {resumo.table_missing ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("passagensRepasses.tableMissing", { defaultValue: "Dados de passagens ainda não sincronizados neste workspace." })}
        </Alert>
      ) : null}

      <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mb: 3 }}>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 160, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t("passagensRepasses.liquidoVendas", { defaultValue: "Líquido das vendas" })}
          </Typography>
          <Typography variant="h5">{fmtMoney(resumo.liquido_vendas)}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 160, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t("passagensRepasses.totalRepassado", { defaultValue: "Já repassado" })}
          </Typography>
          <Typography variant="h5">{fmtMoney(resumo.total_repassado)}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 160, flex: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t("passagensRepasses.reservado", { defaultValue: "Reservado (solicitações)" })}
          </Typography>
          <Typography variant="h5">{fmtMoney(resumo.reservado_solicitacoes)}</Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 2, minWidth: 160, flex: 1, bgcolor: "action.hover" }}>
          <Typography variant="caption" color="text.secondary">
            {t("passagensRepasses.disponivel", { defaultValue: "Disponível para repasse" })}
          </Typography>
          <Typography variant="h5" color="primary.main" fontWeight={700}>
            {fmtMoney(resumo.disponivel_repasse)}
          </Typography>
        </Paper>
      </Stack>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        {t("passagensRepasses.solicitacoesTitle", { defaultValue: "Suas solicitações" })}
      </Typography>
      <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("passagensRepasses.colDate", { defaultValue: "Data" })}</TableCell>
              <TableCell align="right">{t("passagensRepasses.colValue", { defaultValue: "Valor" })}</TableCell>
              <TableCell>{t("passagensRepasses.colStatus", { defaultValue: "Status" })}</TableCell>
              <TableCell>{t("passagensRepasses.colNote", { defaultValue: "Observação" })}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {solicitacoes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <Typography variant="body2" color="text.secondary">
                    {t("passagensRepasses.noSolicitacoes", { defaultValue: "Nenhuma solicitação ainda." })}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              solicitacoes.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{fmtDateTime(row.solicitado_em)}</TableCell>
                  <TableCell align="right">{fmtMoney(row.valor_solicitado)}</TableCell>
                  <TableCell>
                    <Chip size="small" color={STATUS_COLOR[row.status] || "default"} label={row.status} />
                  </TableCell>
                  <TableCell>{row.observacao_cliente || row.observacao_plataforma || "—"}</TableCell>
                  <TableCell align="right">
                    {row.status === "pendente" ? (
                      <Button size="small" onClick={() => cancelMutation.mutate(row.id)} disabled={cancelMutation.isLoading}>
                        {t("passagensRepasses.cancel", { defaultValue: "Cancelar" })}
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        {t("passagensRepasses.lancamentosTitle", { defaultValue: "Repasses efetuados" })}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t("passagensRepasses.colDate", { defaultValue: "Data" })}</TableCell>
              <TableCell align="right">{t("passagensRepasses.colValue", { defaultValue: "Valor" })}</TableCell>
              <TableCell>{t("passagensRepasses.colDesc", { defaultValue: "Descrição" })}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lancamentos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3}>
                  <Typography variant="body2" color="text.secondary">
                    {t("passagensRepasses.noLancamentos", { defaultValue: "Nenhum repasse registrado." })}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              lancamentos.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{fmtDateTime(row.criado_em)}</TableCell>
                  <TableCell align="right">{fmtMoney(row.valor)}</TableCell>
                  <TableCell>{row.descricao || "—"}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("passagensRepasses.requestTitle", { defaultValue: "Solicitar repasse" })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("passagensRepasses.requestIntro", {
              defaultValue: "Disponível: {{valor}}",
              valor: fmtMoney(resumo.disponivel_repasse),
            })}
          </Typography>
          {formError ? <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert> : null}
          <TextField
            fullWidth
            label={t("passagensRepasses.colValue", { defaultValue: "Valor" })}
            type="number"
            inputProps={{ min: 0, step: "0.01" }}
            value={valor}
            onChange={(e) => setValor(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={2}
            label={t("passagensRepasses.colNote", { defaultValue: "Observação" })}
            value={observacao}
            onChange={(e) => setObservacao(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>{t("common.cancel", { defaultValue: "Cancelar" })}</Button>
          <Button variant="contained" onClick={handleSolicitar} disabled={solicitarMutation.isLoading}>
            {t("passagensRepasses.sendRequest", { defaultValue: "Enviar solicitação" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
